import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAvatar,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CContainer,
  CWidgetStatsB,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormCheck,
  CFormSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CCol,
  CSpinner,
  CFormLabel,
  CFormInput,
  CInputGroup,
  CInputGroupText,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCircle,
  cilFactory,
  cilLibraryAdd,
  cilTrash,
  cilZoom,
  cilX,
  cilLockUnlocked,
  cilNotes,
  cilLockLocked,
  cilLowVision,
} from "@coreui/icons";
import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from "components/globals/AlertNotification";
import SearchSelect from "components/forms/SearchSelect";
import userAvatar from "assets/images/avatars/user.png";
import {
  requestApplicationPdfExport,
  triggerDownloadFromUrl,
  waitForApplicationPdfExport,
} from "../_utils/applicationPdfExport";
import { NOTICES } from "./utils";
import { useContentSearch } from "../_hooks/useContentSearch";
import ContentSearchMatches from "../_components/ContentSearchMatches";

const statusOptions = [
  { value: "aberto", label: "Aberto", color: "success" },
  { value: "rascunho", label: "Rascunho", color: "secondary" },
  { value: "desistido", label: "Desistido", color: "danger" },
  { value: "submetido", label: "Submetido", color: "info" },
];

const statusLabelMap = statusOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const statusColorMap = statusOptions.reduce((acc, option) => {
  acc[option.value] = option.color;
  return acc;
}, {});

const statusIconColorMap = {
  aberto: "#22c55e",
  rascunho: "#3b82f6",
  submetido: "var(--cui-primary)",
  desistido: "#ef4444",
};

const statusIconMap = {
  aberto: cilLockUnlocked,
  rascunho: cilNotes,
  submetido: cilLockLocked,
  desistido: cilLowVision,
};

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "";
  const initials = parts.slice(0, 2).map((part) => part[0].toUpperCase());
  return initials.join("");
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-PT");
};

const IdtList = () => {
  const [applications, setApplications] = useState([]);
  const [clients, setClients] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [newApplication, setNewApplication] = useState({
    client: null,
    notice: "",
    isDraft: false,
  });
  const navigate = useNavigate();
  const [exportingIds, setExportingIds] = useState({});
  const [toast, setToast] = useState(false);
  const [previewApplication, setPreviewApplication] = useState(null);
  const exportPollRef = React.useRef(new Set());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [statusFilter, setStatusFilter] = useState("aberto");
  const [noticeFilter, setNoticeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState(null);
  const [contentQuery, setContentQuery] = useState("");
  const contentSearch = useContentSearch("idt", contentQuery, statusFilter);
  const searchHits = contentSearch.hits;

  const setExporting = useCallback((applicationId, value) => {
    setExportingIds((current) => {
      const next = { ...current };
      if (value) {
        next[applicationId] = true;
      } else {
        delete next[applicationId];
      }
      return next;
    });
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam =
        statusFilter && statusFilter !== "todos"
          ? `?status=${statusFilter}`
          : "?status=todos";
      const response = await apiClient.get(`/applications/idt/${statusParam}`);
      setApplications(response.data);
      setPreviewApplication((current) =>
        current
          ? response.data.find((item) => item.id === current.id) || null
          : current,
      );
      setError(false);
    } catch (error) {
      setError(true);
      setToast({
        title: "Candidatuara",
        message: "Ocorreu um erro ao carregar as candidaturas!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const pollApplicationExport = useCallback(
    async (applicationItem) => {
      const exportTaskId = applicationItem?.export_task_id;
      if (!applicationItem?.id || !exportTaskId) return;
      if (exportPollRef.current.has(exportTaskId)) return;

      exportPollRef.current.add(exportTaskId);
      setExporting(applicationItem.id, true);

      try {
        const result = await waitForApplicationPdfExport({
          typology: "idt",
          applicationId: applicationItem.id,
          taskId: exportTaskId,
        });
        await triggerDownloadFromUrl(result.downloadUrl, result.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${applicationItem.client_name || "Candidatura"} gerado com sucesso!`,
          type: "success",
        });
      } catch (error) {
        setToast({
          title: "Pdf",
          message: `Ocorreu um erro ao gerar o PDF ${applicationItem.client_name || "Candidatura"}!`,
          type: "error",
        });
      } finally {
        exportPollRef.current.delete(exportTaskId);
        setExporting(applicationItem.id, false);
        await fetchApplications();
      }
    },
    [fetchApplications, setExporting],
  );

  const downloadPDF = async (idtId, clientName) => {
    if (exportingIds[idtId]) return;
    setExporting(idtId, true);

    try {
      const startResult = await requestApplicationPdfExport({
        typology: "idt",
        applicationId: idtId,
      });

      if (startResult.status === "SUCCESS" && startResult.downloadUrl) {
        await triggerDownloadFromUrl(
          startResult.downloadUrl,
          startResult.filename,
        );
        setToast({
          title: "Pdf",
          message: `PDF ${clientName} gerado com sucesso!`,
          type: "success",
        });
      } else if (startResult.status === "PENDING" && startResult.taskId) {
        await pollApplicationExport({
          id: idtId,
          client_name: clientName,
          export_task_id: startResult.taskId,
        });
        return;
      }

      await fetchApplications();
    } catch (error) {
      setToast({
        title: "Pdf",
        message: `Ocorreu um erro ao gerar o PDF ${clientName}!`,
        type: "error",
      });
    } finally {
      setExporting(idtId, false);
    }
  };

  const handleStatusChange = async (applicationId, status) => {
    try {
      await apiClient.put(`/application/idt/${applicationId}/`, { status });
      setApplications((prev) =>
        prev.map((item) =>
          item.id === applicationId ? { ...item, status } : item,
        ),
      );
      setPreviewApplication((prev) =>
        prev?.id === applicationId ? { ...prev, status } : prev,
      );
      setToast({
        title: "Candidatura",
        message: "Estado atualizado com sucesso!",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Candidatura",
        message: "Não foi possível atualizar o estado.",
        type: "error",
      });
    }
  };

  const handleNoticeChange = async (applicationId, notice) => {
    try {
      await apiClient.put(`/application/idt/${applicationId}/`, { notice });
      setApplications((prev) =>
        prev.map((item) =>
          item.id === applicationId ? { ...item, notice } : item,
        ),
      );
      setPreviewApplication((prev) =>
        prev?.id === applicationId ? { ...prev, notice } : prev,
      );
      setToast({
        title: "Candidatura",
        message: "Aviso atualizado com sucesso!",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Candidatura",
        message: "Não foi possível atualizar o aviso.",
        type: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem a certeza que quer eliminar esta candidatura?")) {
      return;
    }

    apiClient
      .delete(`/application/idt/${id}/`)
      .then(() => {
        triggerAlert("Candidatura eliminada com sucesso", "success");
        setApplications((prev) => prev.filter((item) => item.id !== id));
        setPreviewApplication((prev) => (prev?.id === id ? null : prev));
      })
      .catch(() =>
        setToast({
          title: "Candidatura",
          message: "Ocorreu um erro a eliminar a candidatura",
          type: "error",
        }),
      );
  };

  const handleRowClick = (row) => {
    setPreviewApplication(row);
  };

  const columns = [
    {
      name: "",
      cell: (row) => (
        <CAvatar
          src={row.client_image || undefined}
          size="lg"
          className="border"
          onClick={() => handleRowClick(row)}
          style={{
            width: "48px",
            height: "48px",
            background: row.client_image ? "transparent" : "#eef1f5",
            color: "#495057",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {row.client_image
            ? null
            : getInitials(row.client_name) || <CIcon icon={cilFactory} />}
        </CAvatar>
      ),
      sortable: false,
      width: "84px",
      center: true,
      style: {
        padding: "0.5rem",
      },
    },
    {
      name: "Cliente",
      selector: (row) => row.client_name,
      sortable: true,
      width: "25%",
      wrap: false,
      style: {
        fontWeight: "600",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    {
      name: "Designação",
      selector: (row) => row.designation,
      sortable: true,
      width: "25%",
      // center: true,
      wrap: false,
      style: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    {
      name: "Aviso",
      selector: (row) => row.notice,
      sortable: true,
      width: "10%",
      center: true,
      style: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    {
      name: "Criado",
      selector: (row) => new Date(row.created_at).toLocaleDateString("pt-PT"),
      sortable: true,
      width: "10%",
      center: true,
    },
    {
      name: "Estado",
      cell: (row) => (
        <span
          title={statusLabelMap[row.status] || "Aberto"}
          className="d-inline-flex align-items-center justify-content-center"
          onClick={() => handleRowClick(row)}
          style={{ cursor: "pointer" }}
        >
          <CIcon
            icon={statusIconMap[row.status] || cilCircle}
            style={{ color: statusIconColorMap[row.status] || "#94a3b8" }}
            size="lg"
          />
        </span>
      ),
      sortable: true,
      width: "120px",
      center: true,
    },
    ...(searchHits
      ? [
          {
            name: "Ocorrências",
            selector: (row) => searchHits.get(row.id)?.match_count ?? 0,
            sortable: true,
            width: "130px",
            center: true,
          },
        ]
      : []),
  ];

  const noticesOptions = NOTICES;

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    applications.forEach((applicationItem) => {
      if (applicationItem?.export_task_id) {
        pollApplicationExport(applicationItem);
      }
    });
  }, [applications, pollApplicationExport]);

  useEffect(() => {
    apiClient
      .get("/clients/")
      .then((response) => setClients(response.data))
      .catch((error) =>
        setToast({
          title: "Clientes",
          message: `Ocorreu a carregar os clientes!`,
          type: "error",
        }),
      );
  }, []);

  const filteredApplications = useMemo(() => {
    const noticeValue = noticeFilter?.trim();
    return [...applications]
      .filter((item) => {
        if (noticeValue && item.notice !== noticeValue) {
          return false;
        }
        if (clientFilter && item.client_id !== clientFilter) {
          return false;
        }
        if (searchHits && !searchHits.has(item.id)) {
          return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          (searchHits
            ? searchHits.get(b.id).match_count - searchHits.get(a.id).match_count
            : 0) ||
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
  }, [applications, noticeFilter, clientFilter, searchHits]);

  // Occurrences of the content search in the applications shown.
  const totalMatches = useMemo(
    () =>
      searchHits
        ? filteredApplications.reduce(
            (sum, item) => sum + searchHits.get(item.id).match_count,
            0,
          )
        : 0,
    [filteredApplications, searchHits],
  );
  const occurrencesLabel = `${totalMatches} ${
    totalMatches === 1 ? "ocorrência" : "ocorrências"
  }`;

  const clientOptions = useMemo(() => {
    const map = new Map();
    applications.forEach((item) => {
      if (item.client_id && item.client_name) {
        map.set(item.client_id, item.client_name);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [applications]);

  useEffect(() => {
    if (
      clientFilter &&
      !clientOptions.find((opt) => opt.value === clientFilter)
    ) {
      setClientFilter(null);
    }
  }, [clientFilter, clientOptions]);

  const kpis = useMemo(() => {
    const totalApplications = applications.length;
    const totalClients = new Set(applications.map((app) => app.client_id)).size;
    const totalNotices = new Set(applications.map((app) => app.notice)).size;
    return { totalApplications, totalClients, totalNotices };
  }, [applications]);

  const handleCreate = () => {
    apiClient
      .post("/applications/idt/", {
        client: newApplication.client,
        notice: newApplication.notice,
        status: newApplication.isDraft ? "rascunho" : "aberto",
      })
      .then((response) => {
        navigate(`/candidaturas/idt/${response.data.id}`);
      })
      .catch((error) =>
        setToast({
          title: "Candidatuara",
          message: `Ocorreu a criar a candidatura!`,
          type: "error",
        }),
      );
  };

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CContainer className="px-0">
        <CRow className="g-3 mb-4 align-items-stretch">
          <CCol sm={4} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-total"
              value={"I&DT"}
              title="Tipologia"
              text="Tipologia selecionada"
            />
          </CCol>
          <CCol sm={4} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-pending"
              value={kpis.totalApplications}
              title="Candidaturas"
              text={`Estado: ${statusLabelMap[statusFilter] || "Todos"}`}
            />
          </CCol>
          <CCol sm={4} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-completion"
              value={kpis.totalClients}
              title="Clientes"
              text="Clientes únicos"
            />
          </CCol>
          <CCol sm={4} lg={3}>
            <CWidgetStatsB
              className="resources-kpi h-100 kpi-overdue"
              value={kpis.totalNotices}
              title="Avisos"
              text="Avisos distintos"
            />
          </CCol>
        </CRow>

        <div className="management-filters mb-4">
          <div className="management-filters-header">
            <div className="management-section-title">Filtros</div>
            <CButton
              color="light"
              size="sm"
              onClick={() => {
                setStatusFilter("aberto");
                setNoticeFilter("");
                setClientFilter(null);
                setContentQuery("");
              }}
            >
              Limpar filtros
            </CButton>
          </div>
          <CRow className="g-3 management-filters-grid">
            <CCol xs={12} md={4} lg={3}>
              <CFormLabel>Estado</CFormLabel>
              <CFormSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="aberto">Abertas</option>
                <option value="rascunho">Rascunho</option>
                <option value="submetido">Submetido</option>
                <option value="desistido">Desistido</option>
                <option value="todos">Todos</option>
              </CFormSelect>
            </CCol>
            <CCol xs={12} md={4} lg={4}>
              <CFormLabel>Aviso</CFormLabel>
              <CFormSelect
                value={noticeFilter}
                onChange={(e) => setNoticeFilter(e.target.value)}
              >
                <option value="">Todos os avisos</option>
                {noticesOptions.map((notice) => (
                  <option key={notice.value} value={notice.value}>
                    {notice.label}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol xs={12} md={4} lg={5}>
              <CFormLabel>Cliente</CFormLabel>
              <SearchSelect
                placeholder="Filtrar por cliente"
                value={clientFilter}
                options={clientOptions}
                onChange={(value) => setClientFilter(value)}
                clearable
              />
            </CCol>
            <CCol xs={12}>
              <CFormLabel>Pesquisa no conteúdo</CFormLabel>
              <CInputGroup>
                <CFormInput
                  type="search"
                  value={contentQuery}
                  onChange={(e) => setContentQuery(e.target.value)}
                  placeholder="Texto a procurar nas candidaturas (mín. 2 caracteres; ignora acentos e maiúsculas)"
                />
                {contentSearch.active && (
                  <CInputGroupText>
                    {contentSearch.loading ? (
                      <CSpinner size="sm" />
                    ) : contentSearch.error ? (
                      "Erro na pesquisa"
                    ) : (
                      `${occurrencesLabel} em ${filteredApplications.length} ${
                        filteredApplications.length === 1
                          ? "candidatura"
                          : "candidaturas"
                      }`
                    )}
                  </CInputGroupText>
                )}
              </CInputGroup>
            </CCol>
          </CRow>
          <div className="management-active-filters">
            {[
              statusFilter !== "todos" &&
                `Estado: ${statusLabelMap[statusFilter] || "Todos"}`,
              noticeFilter && `Aviso: ${noticeFilter}`,
              clientFilter &&
                `Cliente: ${
                  clientOptions.find((opt) => opt.value === clientFilter)
                    ?.label || ""
                }`,
              searchHits &&
                `Pesquisa: "${contentQuery.trim()}" (${occurrencesLabel})`,
            ]
              .filter(Boolean)
              .map((filter) => (
                <CBadge key={filter} color="info" className="projects-badge">
                  {filter}
                </CBadge>
              ))}
            {statusFilter === "todos" &&
            !noticeFilter &&
            !clientFilter &&
            !searchHits ? (
              <span className="text-medium-emphasis small">
                Sem filtros aplicados.
              </span>
            ) : null}
          </div>
        </div>

        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-0">Candidaturas I&DT</h3>
            </div>
            <CButton
              color="primary"
              className="d-flex align-items-center"
              onClick={() => setModalVisible(true)}
            >
              <CIcon icon={cilLibraryAdd} size="lg" className="me-2" />
              Criar Candidatura
            </CButton>
          </CCardHeader>
          <CCardBody>
            <DataTableComponent
              columns={columns}
              data={filteredApplications}
              loading={loading || contentSearch.loading}
              error={error}
              onRowClicked={handleRowClick}
            />
          </CCardBody>
        </CCard>
      </CContainer>

      {previewApplication && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewApplication(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewApplication)}
        onHide={() => setPreviewApplication(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Candidatura</div>
              <div className="text-medium-emphasis small">
                #{previewApplication?.id || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewApplication && (
              <>
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(`/candidaturas/idt/${previewApplication.id}`)
                  }
                >
                  <CIcon icon={cilZoom} className="me-1" />
                  Abrir
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  className="text-white"
                  onClick={() =>
                    downloadPDF(
                      previewApplication.id,
                      previewApplication.client_name,
                    )
                  }
                  disabled={
                    Boolean(exportingIds[previewApplication.id]) ||
                    Boolean(previewApplication.export_task_id)
                  }
                >
                  {exportingIds[previewApplication.id] ||
                  previewApplication.export_task_id ? (
                    <CSpinner size="sm" className="me-2" />
                  ) : null}
                  Exportar PDF
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  className="text-white"
                  onClick={() => handleDelete(previewApplication.id)}
                >
                  <CIcon icon={cilTrash} className="me-1" />
                  Eliminar
                </CButton>
              </>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewApplication(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewApplication ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  src={previewApplication.client_image || userAvatar}
                  size="lg"
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {previewApplication.client_name || "-"}
                  </div>
                  <div className="text-medium-emphasis small">
                    {previewApplication.notice || "-"}
                  </div>
                </div>
                <CBadge color={statusColorMap[previewApplication.status]}>
                  {statusLabelMap[previewApplication.status] || "Aberto"}
                </CBadge>
              </div>

              <div>
                <div className="text-uppercase text-medium-emphasis small mb-2">
                  Estado
                </div>
                <CFormSelect
                  value={previewApplication.status || "aberto"}
                  onChange={(e) =>
                    handleStatusChange(previewApplication.id, e.target.value)
                  }
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </CFormSelect>
              </div>

              <div>
                <div className="text-uppercase text-medium-emphasis small mb-2">
                  Aviso
                </div>
                <CFormSelect
                  value={previewApplication.notice || ""}
                  onChange={(e) =>
                    handleNoticeChange(previewApplication.id, e.target.value)
                  }
                >
                  {noticesOptions.map((n) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </CFormSelect>
              </div>

              <ContentSearchMatches hit={searchHits?.get(previewApplication.id)} />

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Designação
                </div>
                <div className="fw-semibold">
                  {previewApplication.designation || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Datas
                </div>
                <div className="fw-semibold">
                  {formatDate(previewApplication.start_date)} •{" "}
                  {formatDate(previewApplication.end_date)}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Localização da operação
                </div>
                <div className="fw-semibold">
                  {(previewApplication.operation_locations || []).join(", ") ||
                    "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  CAEs
                </div>
                <div className="fw-semibold">
                  {(previewApplication.caes || []).join(", ") || "-"}
                </div>
              </div>

              {previewApplication.kickoff_session_id && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small mb-2">
                    Sessão de Arranque
                  </div>
                  <Link
                    to={`/sessoes-arranque/${previewApplication.kickoff_session_id}`}
                  >
                    Abrir sessão de arranque
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted">
              Clique numa candidatura para ver o detalhe.
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>

      <CModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        size="lg"
        backdrop="static"
      >
        <CModalHeader onClose={() => setModalVisible(false)}>
          <CModalTitle>Nova Candidatura</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CForm>
            <SearchSelect
              className="mb-3"
              placeholder="Selecione um Cliente"
              value={newApplication.client}
              options={clients.map((client) => ({
                value: client.id,
                label: client.name,
              }))}
              onChange={(value) =>
                setNewApplication({ ...newApplication, client: value })
              }
              menuPortalTarget={document.body}
            />
            <SearchSelect
              className="mb-3"
              placeholder="Selecione um Aviso"
              value={newApplication.notice}
              options={noticesOptions.map((notice) => ({
                value: notice.value,
                label: notice.label,
              }))}
              onChange={(value) =>
                setNewApplication({ ...newApplication, notice: value })
              }
              menuPortalTarget={document.body}
            />
            <CFormCheck
              label="Criar como rascunho"
              checked={newApplication.isDraft}
              onChange={(e) =>
                setNewApplication({
                  ...newApplication,
                  isDraft: e.target.checked,
                })
              }
            />
          </CForm>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setModalVisible(false)}>
            Cancelar
          </CButton>
          <CButton
            color="primary"
            onClick={handleCreate}
            disabled={!newApplication.client || !newApplication.notice}
          >
            Criar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default IdtList;
